import React, { useState } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { db } from '../../services/db';
import Button from '../common/Button';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import ReactECharts from 'echarts-for-react';
import { motion, AnimatePresence } from 'framer-motion';

const Scorecard = () => {
  const { t, language } = useLanguage();
  const [step, setStep] = useState('intro'); // intro, questions, email, results
  const [currentQ, setCurrentQ] = useState(0);
  const [scores, setScores] = useState({});
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);

  const questions = t('scorecard.questions');
  const categories = ['personal', 'business', 'investment', 'legacy', 'tax'];
  
  // Helper to get category for current question index (3 questions per category)
  const getCategory = (index) => categories[Math.floor(index / 3)];

  const handleAnswer = (value) => {
    setScores(prev => ({ ...prev, [currentQ]: value }));
    if (currentQ < 14) {
      setCurrentQ(prev => prev + 1);
    } else {
      setStep('email');
    }
  };

  const calculateResults = () => {
    let total = 0;
    let catScores = { personal: 0, business: 0, investment: 0, legacy: 0, tax: 0 };
    
    Object.keys(scores).forEach(key => {
      const val = scores[key];
      total += val;
      const cat = getCategory(parseInt(key));
      catScores[cat] += val;
    });

    // Normalize to 100
    const finalScore = Math.round((total / 75) * 100);
    
    // Normalize categories to 100 (max 15 pts per category)
    Object.keys(catScores).forEach(k => {
      catScores[k] = Math.round((catScores[k] / 15) * 100);
    });

    return { finalScore, catScores };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const results = calculateResults();
    await db.saveScore(email, results.finalScore, results.catScores);
    setLoading(false);
    setStep('results');
  };

  const renderIntro = () => (
    <div className="text-center max-w-2xl mx-auto">
      <h2 className="text-3xl md:text-4xl font-bold mb-4">{t('scorecard.title')}</h2>
      <p className="text-xl text-gray-600 mb-8">{t('scorecard.subtitle')}</p>
      <Button onClick={() => setStep('questions')} className="mx-auto">
        {t('scorecard.start')}
      </Button>
    </div>
  );

  const renderQuestion = () => (
    <motion.div 
      key={currentQ}
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="max-w-2xl mx-auto bg-white p-8 rounded-2xl shadow-lg border border-pink-100"
    >
      <div className="flex justify-between text-sm text-pink-600 font-bold mb-4">
        <span>{t(`scorecard.categories.${getCategory(currentQ)}`)}</span>
        <span>{currentQ + 1} / 15</span>
      </div>
      <div className="h-2 bg-gray-100 rounded-full mb-8 overflow-hidden">
        <div 
          className="h-full bg-pink-600 transition-all duration-300"
          style={{ width: `${((currentQ + 1) / 15) * 100}%` }}
        />
      </div>
      <h3 className="text-2xl font-bold mb-8 text-gray-800 min-h-[80px]">
        {questions[currentQ]}
      </h3>
      <div className="grid grid-cols-5 gap-2 md:gap-4">
        {[1, 2, 3, 4, 5].map(val => (
          <button
            key={val}
            onClick={() => handleAnswer(val)}
            className="flex flex-col items-center gap-2 p-4 rounded-xl hover:bg-pink-50 border border-gray-200 hover:border-pink-300 transition-all group"
          >
            <span className="w-8 h-8 rounded-full bg-gray-100 group-hover:bg-pink-600 group-hover:text-white flex items-center justify-center font-bold transition-colors">
              {val}
            </span>
            <span className="text-xs text-center text-gray-500 hidden md:block">
              {val === 1 ? (language === 'es' ? 'Nunca' : 'Never') : val === 5 ? (language === 'es' ? 'Siempre' : 'Always') : ''}
            </span>
          </button>
        ))}
      </div>
    </motion.div>
  );

  const renderEmail = () => (
    <div className="max-w-md mx-auto bg-white p-8 rounded-2xl shadow-xl text-center">
      <h3 className="text-2xl font-bold mb-4">Almost there!</h3>
      <p className="text-gray-600 mb-6">{t('scorecard.emailPrompt')}</p>
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <input
          type="email"
          required
          placeholder="jenny@example.com"
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent outline-none"
          value={email}
          onChange={e => setEmail(e.target.value)}
        />
        <Button type="submit" disabled={loading} className="w-full">
          {loading ? 'Processing...' : 'Ver Resultados / See Results'}
        </Button>
      </form>
    </div>
  );

  const renderResults = () => {
    const { finalScore, catScores } = calculateResults();
    
    const chartOption = {
      radar: {
        indicator: categories.map(c => ({ name: t(`scorecard.categories.${c}`), max: 100 })),
        radius: '70%',
      },
      series: [{
        type: 'radar',
        data: [{
          value: categories.map(c => catScores[c]),
          name: 'Score',
          itemStyle: { color: '#E91E8C' },
          areaStyle: { color: 'rgba(233, 30, 140, 0.2)' }
        }]
      }]
    };

    let level = 'foundation';
    if (finalScore > 40) level = 'growth';
    if (finalScore > 70) level = 'leader';

    return (
      <div className="max-w-4xl mx-auto bg-white p-6 md:p-10 rounded-3xl shadow-2xl border-t-8 border-pink-600">
        <div className="text-center mb-10">
          <h3 className="text-gray-500 text-lg uppercase tracking-widest font-bold mb-2">{t('scorecard.resultTitle')}</h3>
          <div className="relative inline-flex items-center justify-center">
            <svg className="w-40 h-40 transform -rotate-90">
              <circle cx="80" cy="80" r="70" stroke="#f3f4f6" strokeWidth="10" fill="transparent" />
              <circle 
                cx="80" cy="80" r="70" 
                stroke="#E91E8C" 
                strokeWidth="10" 
                fill="transparent"
                strokeDasharray={440}
                strokeDashoffset={440 - (440 * finalScore) / 100}
                className="text-pink-600 transition-all duration-1000 ease-out"
              />
            </svg>
            <span className="absolute text-4xl font-bold text-gray-900">{finalScore}</span>
          </div>
          <h4 className="text-2xl font-bold mt-4 text-pink-600">{t(`scorecard.levels.${level}`)}</h4>
        </div>

        <div className="grid md:grid-cols-2 gap-8 mb-8">
          <div className="h-64">
            <ReactECharts option={chartOption} style={{ height: '100%', width: '100%' }} />
          </div>
          <div className="flex flex-col justify-center space-y-4">
            <h5 className="font-bold text-lg border-b pb-2">Recomendaciones:</h5>
            <ul className="space-y-2 text-gray-700 text-sm">
              <li className="flex items-start gap-2">
                <SafeIcon icon={FiIcons.FiCheckCircle} className="text-green-500 mt-1 flex-shrink-0" />
                <span>Enfócate en aumentar tu margen de beneficio neto.</span>
              </li>
              <li className="flex items-start gap-2">
                <SafeIcon icon={FiIcons.FiCheckCircle} className="text-green-500 mt-1 flex-shrink-0" />
                <span>Diversifica tus inversiones fuera de tu negocio principal.</span>
              </li>
              <li className="flex items-start gap-2">
                <SafeIcon icon={FiIcons.FiCheckCircle} className="text-green-500 mt-1 flex-shrink-0" />
                <span>Revisa tu estructura legal para optimización fiscal.</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="flex justify-center">
          <Button onClick={() => window.open('https://calendly.com', '_blank')}>
            {t('scorecard.cta')}
          </Button>
        </div>
      </div>
    );
  };

  return (
    <section id="scorecard" className="py-20 bg-gray-50">
      <div className="container mx-auto px-4">
        <AnimatePresence mode="wait">
          {step === 'intro' && renderIntro()}
          {step === 'questions' && renderQuestion()}
          {step === 'email' && renderEmail()}
          {step === 'results' && renderResults()}
        </AnimatePresence>
      </div>
    </section>
  );
};

export default Scorecard;